import React, { useState, useMemo, useEffect } from 'react';
import { Row, Col, Card, Spinner } from 'react-bootstrap';
import Filter from './Filter';
import IntensityGraphCard from './IntensityGraphCard';
import IntensitySummaryCard from './IntensitySummaryCard';
import { apiCall } from '../../../../../../_services/apiCall';
import config from "../../../../../../../src/config/config.json";
import { generateTimePeriodOptions, getStartingMonth } from '../../../../../CarbonFootPrinting/utils/PeriodCalculationUtils';
import { getSource, fetchFrequency } from '../../../../../Training/training-dashboard/services/trainingService';

export const Intensity = ({ financialYearId: dashboardFYId, financialYear: fyList = [] }) => {
  const formatLabel = (str) => str.replace(/([A-Z])/g, ' $1').trim();
  const kpiList = ["Revenue (In Cr)", "Total Built-up Area (sq. ft)", "Total Patient Served", "Total Operating Beds"];

  const latestFY = useMemo(() => fyList.sort((a, b) => parseInt(b.financial_year_value) - parseInt(a.financial_year_value))[0], [fyList]);

  const [financialYearId, setFinancialYearId] = useState('');
  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [timePeriodOptions, setTimePeriodOptions] = useState([]);
  const [selectedPeriods, setSelectedPeriods] = useState([]); 
  const [selectedPeriodsValue, setSelectedPeriodsValue] = useState([]); 
  const [availableTypes, setAvailableTypes] = useState([]);
  const [selectedIntensityType, setSelectedIntensityType] = useState(null);
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const activeId = dashboardFYId || latestFY?.id || '31';
    setFinancialYearId(activeId.toString());
  }, [dashboardFYId, latestFY]);

  useEffect(() => {
    const init = async () => {
      if (!financialYearId) return;
      setLoading(true);
      try {
        const [locs, freq, res] = await Promise.all([
          getSource(), fetchFrequency(financialYearId),
          apiCall(`${config.POSTLOGIN_API_URL_COMPANY}intensity`, {}, { financialYearId }, "GET")
        ]);
        const data = res?.data || res?.result || [];
        setRawData(data);
        setLocations(locs || []);
        const types = [...new Set(data.map(item => item.type))].map(t => ({ value: t, label: formatLabel(t) }));
        setAvailableTypes(types);
        if (types.length > 0) setSelectedIntensityType(types[0]);

        if (locs?.length) setSelectedLocations(locs.map(l => l.id));
        const periods = generateTimePeriodOptions(freq || 'MONTHLY', getStartingMonth());
        setTimePeriodOptions(periods);
        if (periods?.length) setSelectedPeriods(periods.map(p => p.value));
      } catch (err) { console.error(err); } finally { setLoading(false); }
    };
    init();
  }, [financialYearId]);

  useEffect(() => {
    if (!financialYearId || !selectedPeriods?.length) return;
    const activeFY = fyList.find(f => f.id.toString() === financialYearId);
    const year = parseInt(activeFY?.financial_year_value.split("-")[0]);
    const ranges = selectedPeriods.map(p => {
      const start = getStartingMonth();
      const m = ((start - 1 + (p - 1)) % 12) + 1;
      const y = year + Math.floor((start - 1 + (p - 1)) / 12);
      const fromDate = `${y}-${m < 10 ? `0${m}` : m}`;
      return { fromDate, label: timePeriodOptions.find(opt => opt.value === p)?.label || fromDate };
    });
    setSelectedPeriodsValue(ranges);
  }, [selectedPeriods, financialYearId, timePeriodOptions, fyList]);

  const filterProps = {
    fyOptions: fyList.map(fy => ({ value: fy.id.toString(), label: fy.financial_year_value })),
    locationOptions: locations.map(l => ({ value: l.id, label: l.unitCode })),
    periodOptions: timePeriodOptions.map(p => ({ value: p.value, label: p.label })),
    intensityTypeOptions: availableTypes,
    filters: {
      financialYear: { value: financialYearId, label: fyList.find(f => f.id.toString() === financialYearId)?.financial_year_value },
      locations: selectedLocations.map(id => ({ value: id, label: locations.find(l => l.id === id)?.unitCode })),
      periods: selectedPeriods.map(id => ({ value: id, label: timePeriodOptions.find(p => p.value === id)?.label })),
      intensityType: selectedIntensityType
    },
    setFilters: (f) => {
      if (f.financialYear) setFinancialYearId(f.financialYear.value);
      setSelectedLocations(f.locations ? f.locations.map(l => l.value) : []);
      setSelectedPeriods(f.periods ? f.periods.map(p => p.value) : []);
      setSelectedIntensityType(f.intensityType);
    }
  };

  return (
    <div className="p-3">
      {/* Filters */}
      <Card className="mb-4 shadow-sm border-0">
        <Card.Body>
          <Row>
            <Filter {...filterProps} />
          </Row>
        </Card.Body>
      </Card>

      {loading ? (
        <div className="text-center p-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          {/* 🔹 Intensity Summary Cards */}
          <Row className="mb-4">
            {kpiList.map((kpi, idx) => (
              <Col md={3} sm={6} xs={12} key={`summary-${idx}`} className="mb-3">
                <IntensitySummaryCard
                  rawData={rawData}
                  selectedIntensityType={selectedIntensityType}
                  selectedPeriodsValue={selectedPeriodsValue}
                  selectedLocations={selectedLocations}
                  targetKpi={kpi}
                />
              </Col>
            ))}
          </Row>

          {/* 🔹 Intensity Graph Cards */}
          <Row>
            {kpiList.map((kpi, idx) => (
              <Col md={6} key={`graph-${idx}`} className="mb-4">
                <IntensityGraphCard
                  rawData={rawData}
                  selectedIntensityType={selectedIntensityType}
                  selectedPeriodsValue={selectedPeriodsValue}
                  selectedLocations={selectedLocations}
                  targetKpi={kpi}
                />
              </Col>
            ))}
          </Row>
        </>
      )}
    </div>
  );

};